import { useLocalStorage } from '@vueuse/core';
import { computed } from 'vue';
import type { Column } from '../components/DataTable/DataTable.types';

const ACTIONS_COLUMN_VALUE = 'actions';

type ColumnVisibilityStore = Record<string, string[]>;

export function useColumnVisibility(
  namespace: string,
  nonHideableColumns: string[] = [],
) {
  const store = useLocalStorage<ColumnVisibilityStore>('column-visibility', {});

  const hiddenColumns = computed({
    get: () => store.value[namespace] ?? [],
    set: (value: string[]) => {
      store.value = { ...store.value, [namespace]: value };
    },
  });

  const isActionsColumn = (column: Column): boolean => {
    return column.value === ACTIONS_COLUMN_VALUE;
  };

  const isColumnHideable = (column: Column, index: number): boolean => {
    if (index === 0) return false;
    if (isActionsColumn(column)) return false;
    return !nonHideableColumns.includes(column.value);
  };

  const isColumnVisible = (columnValue: string): boolean => {
    return !hiddenColumns.value.includes(columnValue);
  };

  const toggleColumn = (column: Column, index: number): void => {
    if (!isColumnHideable(column, index)) return;

    const columnValue = column.value;
    const current = hiddenColumns.value;

    if (current.includes(columnValue)) {
      hiddenColumns.value = current.filter((v) => v !== columnValue);
    } else {
      hiddenColumns.value = [...current, columnValue];
    }
  };

  const getHideableColumns = (columns: Column[]): Column[] => {
    return columns.filter((col, index) => isColumnHideable(col, index));
  };

  const areAllVisible = (columns: Column[]): boolean => {
    const hideable = getHideableColumns(columns);
    return hideable.every((col) => isColumnVisible(col.value));
  };

  const areSomeVisible = (columns: Column[]): boolean => {
    const hideable = getHideableColumns(columns);
    const visibleCount = hideable.filter((col) =>
      isColumnVisible(col.value),
    ).length;
    return visibleCount > 0 && visibleCount < hideable.length;
  };

  const toggleAll = (columns: Column[], visible: boolean): void => {
    const hideable = getHideableColumns(columns);
    if (visible) {
      hiddenColumns.value = hiddenColumns.value.filter(
        (v) => !hideable.some((col) => col.value === v),
      );
    } else {
      const toHide = hideable.map((col) => col.value);
      const existing = hiddenColumns.value.filter((v) => !toHide.includes(v));
      hiddenColumns.value = [...existing, ...toHide];
    }
  };

  const getVisibleColumns = (columns: Column[]): Column[] => {
    return columns.filter(
      (col, index) =>
        !isColumnHideable(col, index) || isColumnVisible(col.value),
    );
  };

  return {
    hiddenColumns,
    isColumnHideable,
    isColumnVisible,
    toggleColumn,
    toggleAll,
    getVisibleColumns,
    areAllVisible,
    areSomeVisible,
  };
}
